import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseQueryValue } from '@netiva/classifieds-common';

import { AdListSearchParams, FilterStatus } from '../types';

const setParam = <T>(params: URLSearchParams, name: string, value?: T) => {
  if (value) {
    params.set(name, value?.toString());
  } else {
    params.delete(name);
  }
};

export const useAdListSearchParams = (): [
  AdListSearchParams,
  (params: AdListSearchParams, replace?: boolean) => void,
] => {
  const [_searchParams, _setSearchParams] = useSearchParams();

  const searchParams = useMemo<AdListSearchParams>(
    () => ({
      pageIndex: parseQueryValue(_searchParams.get('p'), parseInt, 1) - 1,
      platform: _searchParams.get('platform') || '',
      status: (_searchParams.get('status') as FilterStatus) || 'active',
      query: _searchParams.get('q') || '',
      topListed: _searchParams.get('topListed') === 'true',
      categoryId: parseQueryValue(_searchParams.get('categoryId'), parseInt),
    }),
    [_searchParams]
  );

  const setSearchParams = useCallback(
    (params: AdListSearchParams, replace = false) => {
      const urlSearchParams = new URLSearchParams();
      if (params.pageIndex && params.pageIndex > 0) {
        setParam(urlSearchParams, 'p', params.pageIndex + 1);
      }
      setParam(urlSearchParams, 'q', params.query);
      setParam(urlSearchParams, 'platform', params.platform);
      setParam(urlSearchParams, 'status', params.status);
      setParam(urlSearchParams, 'topListed', params.topListed);
      setParam(urlSearchParams, 'categoryId', params.categoryId);

      // guard to prevent duplicate navigation
      if (urlSearchParams.toString() === _searchParams.toString()) return;

      _setSearchParams(urlSearchParams, { replace });
    },
    [_searchParams, _setSearchParams]
  );

  return [searchParams, setSearchParams];
};
